import type { PrefetchedConsentPreferences } from '@atlassian/browser-storage-controls';
import { serializeConsents } from '@atlassian/browser-storage-controls';

import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures';
import { cfetch } from '@confluence/network';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient';
import { isLocal } from '@confluence/ssr-utilities/entry-points/env';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs';
import { constructCompatibleRequestInit } from '@confluence/network/entry-points/constructCompatibleRequestInit';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext';

export const COOKIE_PREFERENCES_QUERY = 'CookiePreferencesQuery';

export const preloadCookiePreferences = async () => {
	if (window.__COOKIE_CONSENT_PREFERENCES__) {
		return window.__COOKIE_CONSENT_PREFERENCES__;
	}

	const { cookiesConsentToken, cookiesFallbackToken, userId, perimeter } = getSSRRenderInputs();

	// If the consent token cookie exist (user has already set their preferences), there is no need to
	// fetch the preferences from ConsentHub
	if (cookiesConsentToken && cookiesFallbackToken) {
		return (window.__COOKIE_CONSENT_PREFERENCES__ = {
			consentToken: cookiesConsentToken,
			fallbackConsentToken: cookiesFallbackToken,
		} as PrefetchedConsentPreferences);
	}

	if (!userId) {
		const consentToken = serializeConsents({});
		window.__COOKIE_CONSENT_PREFERENCES__ = consentToken ? { consentToken } : null;
		return window.__COOKIE_CONSENT_PREFERENCES__;
	}

	if (perimeter === 'fedramp-moderate') {
		return (window.__COOKIE_CONSENT_PREFERENCES__ = null);
	}

	// Only compute hostEnv when we actually need to make a network call
	const { environment } = getPreloaderFnContext();
	const hostEnv = environment === 'PRODUCTION' ? 'prod' : 'staging';

	return SSRMeasures.run(
		'ssr-app/prepare/preloadQuery/fetch:CookieConsentPreferences',
		async () => {
			let result: any = null;
			const isLocalPillar = isLocal();
			const url = isLocalPillar
				? '/gateway/api/consenthub/session/user/consents/cookies'
				: `https://atl-preferences-center-elb.${hostEnv}.atl-paas.net/rest/user/${userId}/consents/cookies`;

			try {
				const res = await cfetch(
					url,
					constructCompatibleRequestInit({
						method: 'GET',
						headers: {
							'Content-Type': 'application/json',
							// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
							...(Boolean(process.env.REACT_SSR) &&
							Boolean(process.env.BIFROST_TEMPLATE_BUILD) &&
							!isLocalPillar
								? { 'x-ssr-service-name': 'atl-preferences-center' }
								: {}),
						},
						credentials: 'include',
					}),
				);
				if (!res.ok) {
					throw new Error(`Cannot load Cookie Consent Preferences: ${res.statusText}`);
				}

				result = await res.json();
			} catch (e) {
				ssrPrepareErrorsClient.submitError(
					new Error(`Fetching cookie preferences failed: ${e.message}`),
				);
			}
			if (result && Object.keys(result).length) {
				return (window.__COOKIE_CONSENT_PREFERENCES__ = Object.assign({}, result, {
					fallbackConsentToken: cookiesFallbackToken,
				}));
			} else {
				return (window.__COOKIE_CONSENT_PREFERENCES__ = null);
			}
		},
	);
};
